import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, Alert, Divider,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface FlowStep {
  label: string;
  detail?: string;
}
export interface FlowSection {
  title: string;
  steps: FlowStep[];
}
export interface HelpSection {
  title: string;
  items: string[];
}

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  intro?: string;
  warning?: string;
  flows: FlowSection[];
  sections?: HelpSection[];
  notes?: string[];
}

const StepCard: React.FC<{ index: number; step: FlowStep }> = ({ index, step }) => (
  <Box
    sx={{
      display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, borderRadius: 2,
      border: '1px solid #e2e8f0', bgcolor: '#f8fafc', width: '100%',
    }}
  >
    <Box
      sx={{
        width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff',
        fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
      }}
    >
      {index}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{step.label}</Typography>
      {step.detail && <Typography variant="caption" color="text.secondary">{step.detail}</Typography>}
    </Box>
  </Box>
);

/** Explains a feature's flow with a simple vertical flowchart + notes. */
const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose, title, intro, warning, flows, sections, notes }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
    <DialogContent dividers>
      {intro && <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">{intro}</Typography>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }}>{warning}</Alert>}
      {flows.map((flow, fi) => (
        <Box key={fi} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>{flow.title}</Typography>
          <Stack spacing={0.5} alignItems="center">
            {flow.steps.map((s, i) => (
              <React.Fragment key={i}>
                <StepCard index={i + 1} step={s} />
                {i < flow.steps.length - 1 && <ArrowDownwardIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      ))}
      {sections?.map((sec, si) => (
        <Box key={si} sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>{sec.title}</Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {sec.items.map((n, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{n}</Typography>
            ))}
          </Box>
        </Box>
      ))}
      {notes && notes.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Notes</Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {notes.map((n, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{n}</Typography>
            ))}
          </Box>
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">Got it</Button>
    </DialogActions>
  </Dialog>
);

export default HelpDialog;
